import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { EnvelopeSimple, Phone, MapPin, ArrowUpRight } from "@phosphor-icons/react";
import { COMPANY, SERVICES } from "@/data/content";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const FAQS = [
  {
    q: "What is your typical response time?",
    a: "We respond to all inquiries within 24 hours on business days. For urgent matters, please call us directly.",
  },
  {
    q: "Do you offer free consultations?",
    a: "Yes — we offer a free 30-minute discovery call to scope your requirement before any commercial conversation.",
  },
  {
    q: "Do you work with international clients?",
    a: "Absolutely. We currently serve clients across India, KSA, UAE, Kuwait and beyond — comfortable with multiple time zones.",
  },
  {
    q: "What sized projects do you take on?",
    a: "From rapid 4-week MVPs to multi-quarter ERP implementations and security retainers. We scope honestly upfront.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    interest: SERVICES[0].name,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please write at least 10 characters in your message.");
      return;
    }
    if (!WEB3FORMS_KEY) {
      // Fallback: open the user's mail client if the form service isn't configured yet.
      const subject = encodeURIComponent(`New inquiry — ${form.interest}`);
      const body = encodeURIComponent(
        `Name: ${form.full_name}\nEmail: ${form.email}\nPhone: ${form.phone}\nCompany: ${form.company || "—"}\nInterest: ${form.interest}\n\nMessage:\n${form.message}`,
      );
      window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
      toast.message("Opening your mail client…", {
        description: "We're routing your message via email.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        from_name: form.full_name,
        replyto: form.email,
        subject: `New Theta Dynamics inquiry — ${form.interest}`,
        botcheck: "",
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        interest: form.interest,
        message: form.message,
      };
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || "Submission failed");
      }
      toast.success("Thanks — your message has been received. Our team will reach out within 24 hours.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        interest: SERVICES[0].name,
        message: "",
      });
    } catch (err) {
      toast.error(err?.message || "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-5">Contact</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900">
              Tell us what you're building. <span className="text-[#1D4ED8]">We'll engineer the rest.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
              Reach out for ERP, custom software, security audits, AI/ML or training. Most engagements start with a free 30-minute discovery call.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          {/* Form */}
          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="card-flat p-7 md:p-10 flex flex-col gap-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input
                    required
                    minLength={2}
                    value={form.full_name}
                    onChange={update("full_name")}
                    type="text"
                    name="full_name"
                    data-testid="contact-input-name"
                    className="form-input"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    required
                    value={form.email}
                    onChange={update("email")}
                    type="email"
                    name="email"
                    data-testid="contact-input-email"
                    className="form-input"
                    placeholder="jane@company.com"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Phone *">
                  <input
                    required
                    minLength={5}
                    value={form.phone}
                    onChange={update("phone")}
                    type="tel"
                    name="phone"
                    data-testid="contact-input-phone"
                    className="form-input"
                    placeholder="+91 ..."
                  />
                </Field>
                <Field label="Company">
                  <input
                    value={form.company}
                    onChange={update("company")}
                    type="text"
                    name="company"
                    data-testid="contact-input-company"
                    className="form-input"
                    placeholder="Acme Inc."
                  />
                </Field>
              </div>

              <Field label="What are you interested in?">
                <select
                  value={form.interest}
                  onChange={update("interest")}
                  data-testid="contact-input-interest"
                  className="form-input"
                >
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.name}>{s.name}</option>
                  ))}
                  <option value="Products">Products (KarnatakaCET / Anciens Network)</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </Field>

              <Field label="Message *">
                <textarea
                  required
                  minLength={10}
                  value={form.message}
                  onChange={update("message")}
                  rows={6}
                  name="message"
                  data-testid="contact-input-message"
                  className="form-input resize-none"
                  placeholder="Tell us a bit about your project, timelines and key challenges."
                />
              </Field>

              <div className="flex items-center justify-between gap-4 mt-2">
                <p className="text-xs font-mono text-slate-500">
                  Your information stays between us. No third-party sharing.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send message"} <ArrowUpRight size={16} weight="bold" />
                </button>
              </div>
            </form>

            <style>{`
              .form-input {
                width: 100%;
                background: #ffffff;
                border: 1px solid #CBD5E1;
                color: #0F172A;
                padding: 0.95rem 1rem;
                font-size: 13px;
                font-family: 'JetBrains Mono', monospace;
                outline: none;
                transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
              }
              .form-input::placeholder { color: #94A3B8; }
              .form-input:focus {
                border-color: #1D4ED8;
                box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.12);
              }
            `}</style>
          </div>

          {/* Contact info */}
          <div className="md:col-span-5 flex flex-col gap-5">
            <div className="card-flat p-7 md:p-8">
              <div className="overline mb-5">Reach us</div>
              <ul className="space-y-5">
                <li className="flex gap-4 items-start" data-testid="contact-info-email">
                  <EnvelopeSimple size={22} weight="bold" className="text-[#1D4ED8] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="block text-sm text-slate-900 hover:text-[#1D4ED8] transition-colors mt-1">{COMPANY.email}</a>
                  </div>
                </li>
                <li className="flex gap-4 items-start" data-testid="contact-info-phone">
                  <Phone size={22} weight="bold" className="text-[#1D4ED8] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Call us</div>
                    {COMPANY.phones.map((p) => (
                      <a
                        key={p}
                        href={`tel:${p.replace(/[^+0-9]/g, '')}`}
                        className="block text-sm text-slate-900 hover:text-[#1D4ED8] transition-colors mt-1"
                      >
                        {p}
                      </a>
                    ))}
                    <div className="text-xs text-slate-500 mt-1">{COMPANY.hours}</div>
                  </div>
                </li>
                <li className="flex gap-4 items-start" data-testid="contact-info-address">
                  <MapPin size={22} weight="bold" className="text-[#1D4ED8] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Visit us</div>
                    <div className="text-sm text-slate-900 mt-1">{COMPANY.address}</div>
                    <div className="text-sm text-slate-600">{COMPANY.city}</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="card-flat p-7 md:p-8">
              <div className="overline mb-5">FAQ</div>
              <div className="flex flex-col divide-y divide-slate-200">
                {FAQS.map((f, i) => (
                  <motion.details
                    key={f.q}
                    data-testid={`faq-${i}`}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="group py-4 [&_summary::-webkit-details-marker]:hidden"
                  >
                    <summary className="cursor-pointer flex items-center justify-between gap-4 text-sm text-slate-900 font-medium">
                      {f.q}
                      <span className="text-[#1D4ED8] font-mono text-base group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </motion.details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-600 font-bold">{label}</span>
      {children}
    </label>
  );
}
